/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  UseMyLocation component
 */

import { Box, type BoxProps } from '@chakra-ui/react'
import { Location } from '@zcode-ui/icons'
import React, { useCallback } from 'react'
import { a11yButtonHandler } from '../../../../shared/utils/a11y'
import { useAutocompleteContext } from '../AutocompleteContext'
import type { AutocompleteOrigin, AutocompleteTheme } from '../constants'
import { getIdFromOrigin } from '../helpers'

export interface UseMyLocationProps {
  index: number
  onClickGetCurrentPosition: () => void
  origin?: AutocompleteOrigin
  theme: AutocompleteTheme
}

export const UseMyLocation = ({
  index,
  onClickGetCurrentPosition,
  origin,
  theme,
}: UseMyLocationProps) => {
  const { hoveredIndex, setForceContextualMenuOpen, setIsFocused, setHoveredIndex, setSearchTerm } =
    useAutocompleteContext()

  const onClearAndBlur = useCallback(() => {
    setSearchTerm('')
    setIsFocused(false)
    setForceContextualMenuOpen(false)
  }, [setSearchTerm, setIsFocused, setForceContextualMenuOpen])
  const onMouseEnter = useCallback(() => setHoveredIndex(0), [setHoveredIndex])
  const onMouseLeave = useCallback(() => setHoveredIndex(-1), [setHoveredIndex])

  const onClick = useCallback(() => {
    onClickGetCurrentPosition()
    onClearAndBlur()
  }, [onClickGetCurrentPosition, onClearAndBlur])
  const hoverStyles: BoxProps = {
    backgroundColor: 'gray.50',
    borderLeftColor: 'pink.600',
  }
  const hovered = index === hoveredIndex

  return (
    <Box<'div'>
      _hover={hoverStyles}
      aria-selected={hovered}
      backgroundColor={hovered ? 'gray.50' : 'transparent'}
      borderLeftColor={hovered ? 'pink.600' : 'transparent'}
      borderLeftStyle="solid"
      borderLeftWidth="2px"
      className={theme.inputBtn}
      id={`${getIdFromOrigin(origin)}-results-0`}
      onClick={onClick}
      onKeyPress={a11yButtonHandler(onClickGetCurrentPosition)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="option"
      tabIndex={-1}
    >
      <Location variant="Autocomplete" />
      <Box<'div'>
        className={theme.primaryText}
        color="gray.800"
        fontSize="md"
        marginLeft={{ base: 0, lg: 3 }}
      >
        Use your current location
      </Box>
    </Box>
  )
}
